import { api } from '@/helpers/apiClient'
import { toQueryString } from '@/helpers/queryString'
import logger, { toErrorMessage } from '@/helpers/logger'
import {
  irrigationEstimateDtoSchema,
  irrigationSiteDtoSchema,
  type IrrigationEstimateDto,
  type IrrigationSiteDto,
} from '@lib/dto/irrigation.dto'
import type { IrrigationSyncOperation } from '@lib/input/irrigation.input'
import type { IIrrigationService } from '@/types/IrrigationService.types'

/** `?visitId=…` for a visit, nothing for the site's current one. */
function visitQuery(visitId: string | null): string {
  return visitId === null ? '' : toQueryString({ visitId })
}

/** Service for a site's irrigation system. */
class IrrigationService implements IIrrigationService {
  /**
   * The site's system, with the problems one visit found: `visitId`, or the
   * site's current irrigation visit when it is left out or null.
   */
  async getSite(
    siteId: string,
    visitId: string | null = null,
  ): Promise<IrrigationSiteDto> {
    try {
      return await api.get(
        `/sites/${encodeURIComponent(siteId)}/irrigation${visitQuery(visitId)}`,
        irrigationSiteDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to load the irrigation system', {
        error: toErrorMessage(error),
        siteId,
      })
      throw error
    }
  }

  async estimate(
    siteId: string,
    visitId: string | null = null,
  ): Promise<IrrigationEstimateDto> {
    try {
      return await api.get(
        `/sites/${encodeURIComponent(siteId)}/irrigation/estimate${visitQuery(visitId)}`,
        irrigationEstimateDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to price the irrigation repairs', {
        error: toErrorMessage(error),
        siteId,
      })
      throw error
    }
  }

  async sync(
    siteId: string,
    operations: IrrigationSyncOperation[],
    /** The visit this phone is working in, whose problems to answer with. */
    visitId: string | null = null,
  ): Promise<IrrigationSiteDto> {
    try {
      return await api.post(
        `/sites/${encodeURIComponent(siteId)}/irrigation/sync`,
        { operations, visitId },
        irrigationSiteDtoSchema,
      )
    } catch (error) {
      // Logged at warn: on a phone in a park a failed sync is the normal case,
      // and the queue keeps the work until the next try.
      logger.warn('Irrigation sync did not complete', {
        error: toErrorMessage(error),
        siteId,
        operations: operations.length,
      })
      throw error
    }
  }
}

export const irrigationService: IIrrigationService = new IrrigationService()
